import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Stack,
  Typography,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import LockResetIcon from "@mui/icons-material/LockReset";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { request } from "../../config/request.ts";
import TextField from "../../shared/TextField/index.tsx";

interface ChangePasswordDialogProps {
  open: boolean;
  userId: number;
  userName: string;
  requireCurrentPassword?: boolean;
  onClose: () => void;
}

export default function ChangePasswordDialog({
  open,
  userId,
  userName,
  requireCurrentPassword = false,
  onClose,
}: ChangePasswordDialogProps) {
  const formik = useFormik({
    initialValues: {
      current_password: "",
      password: "",
      password_confirmation: "",
    },
    validationSchema: Yup.object({
      current_password: requireCurrentPassword
        ? Yup.string().required("Current password is required")
        : Yup.string(),
      password: Yup.string()
        .min(6, "Password must be at least 6 characters")
        .required("New password is required"),
      password_confirmation: Yup.string()
        .oneOf([Yup.ref("password")], "Passwords must match")
        .required("Confirm password is required"),
    }),
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      const payload = {
        password: values.password,
        password_confirmation: values.password_confirmation,
        ...(requireCurrentPassword
          ? { current_password: values.current_password }
          : {}),
      };

      try {
        const response = await request.put(`users/${userId}/password`, payload);
        if (response?.data?.status) {
          toast.success(
            response?.data?.message || "Password updated successfully!",
          );
          resetForm();
          onClose();
        }
      } catch (error: any) {
        toast.error(
          error?.response?.data?.message ||
            "An error occurred. Please try again.",
        );
      } finally {
        setSubmitting(false);
      }
    },
  });

  const [visibleFields, setVisibleFields] = useState({
    current_password: false,
    password: false,
    password_confirmation: false,
  });

  useEffect(() => {
    if (open) {
      formik.resetForm();
      setVisibleFields({
        current_password: false,
        password: false,
        password_confirmation: false,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const toggleVisibility = (field: keyof typeof visibleFields) => {
    setVisibleFields((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const visibilityAdornment = (field: keyof typeof visibleFields) => ({
    endAdornment: (
      <InputAdornment position="end">
        <IconButton
          size="small"
          edge="end"
          onClick={() => toggleVisibility(field)}
          aria-label={visibleFields[field] ? "Hide password" : "Show password"}
        >
          {visibleFields[field] ? (
            <VisibilityOffIcon fontSize="small" />
          ) : (
            <VisibilityIcon fontSize="small" />
          )}
        </IconButton>
      </InputAdornment>
    ),
  });

  const handleClose = () => {
    formik.resetForm();
    setVisibleFields({
      current_password: false,
      password: false,
      password_confirmation: false,
    });
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "20px",
          overflow: "hidden",
          boxShadow: "0 24px 70px rgba(26, 31, 58, 0.22)",
        },
      }}
    >
      <form onSubmit={formik.handleSubmit}>
        <DialogTitle sx={{ p: 0 }}>
          <Box
            sx={{
              height: 6,
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            }}
          />
          <Box
            sx={{
              px: 3,
              py: 2.5,
              background: "linear-gradient(135deg, #f8faff 0%, #f3efff 100%)",
              borderBottom: "1px solid #ece9ff",
            }}
          >
            <Stack direction="row" spacing={1.75} alignItems="center">
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: "14px",
                  background:
                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 8px 20px rgba(102, 126, 234, 0.35)",
                }}
              >
                <LockResetIcon sx={{ color: "#fff", fontSize: 26 }} />
              </Box>
              <Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 800,
                    color: "#1a1f3a",
                    fontSize: "1.15rem",
                    lineHeight: 1.3,
                  }}
                >
                  Change Password
                </Typography>
                <Typography sx={{ color: "#64748b", fontSize: "0.82rem" }}>
                  Set a new password for{" "}
                  <Box
                    component="span"
                    sx={{ fontWeight: 700, color: "#4c51bf" }}
                  >
                    {userName}
                  </Box>
                </Typography>
              </Box>
            </Stack>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ px: 3, pt: 3.5, pb: 1, mt: 2 }}>
          <Stack spacing={2.5}>
            {requireCurrentPassword && (
              <TextField
                name="current_password"
                label="Current Password"
                type={visibleFields.current_password ? "text" : "password"}
                formik={formik}
                autoFocus
                InputProps={visibilityAdornment("current_password")}
              />
            )}
            <TextField
              name="password"
              label="New Password"
              type={visibleFields.password ? "text" : "password"}
              formik={formik}
              autoFocus={!requireCurrentPassword}
              InputProps={visibilityAdornment("password")}
            />
            <TextField
              name="password_confirmation"
              label="Confirm New Password"
              type={visibleFields.password_confirmation ? "text" : "password"}
              formik={formik}
              InputProps={visibilityAdornment("password_confirmation")}
            />
          </Stack>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              mt: 2.25,
              color: "#8a9bb0",
              fontSize: "0.75rem",
            }}
          >
            <LockOutlinedIcon sx={{ fontSize: 15 }} />
            Password must be at least 6 characters long.
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            pt: 2,
            pb: 2.5,
            gap: 1,
            borderTop: "1px solid #f1f5f9",
          }}
        >
          <Button
            onClick={handleClose}
            sx={{
              borderRadius: "10px",
              px: 2.5,
              py: 0.75,
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.85rem",
              color: "#64748b",
              "&:hover": {
                background: "#f8fafc",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={formik.isSubmitting}
            sx={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              borderRadius: "10px",
              px: 3,
              py: 0.75,
              textTransform: "none",
              fontWeight: 700,
              fontSize: "0.85rem",
              boxShadow: "0 6px 16px rgba(102, 126, 234, 0.35)",
              "&:hover": {
                background: "linear-gradient(135deg, #5a6fd8 0%, #6a3f91 100%)",
                boxShadow: "0 8px 22px rgba(102, 126, 234, 0.45)",
              },
              "&.Mui-disabled": {
                background: "#cbd5e1",
                color: "#fff",
              },
            }}
          >
            {formik.isSubmitting ? "Updating..." : "Update Password"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
